/**
 * Finance Module — Formatters
 *
 * Shared formatting utilities for amounts, dates, and percentages.
 * Currency and locale resolve from the tenant profile; DD/MM/YYYY dates.
 */

import { getTenantMoney } from '@/lib/tenant-format'
import { roundMoney } from '@/lib/money'

/**
 * Round a currency value to 2 decimal places (minor-unit precision).
 * Use this around any arithmetic on amounts to prevent JS float artifacts
 * like 50000 - 12521.05 - 35476.25 = 2002.699999999997 from leaking into
 * stored values, API requests, or displayed totals.
 *
 * Delegates to the one helper that agrees with the server's
 * `BigDecimal.setScale(2, HALF_UP)`. The previous `Math.round(value * 100) / 100`
 * disagreed on `1.005` (float artefact) and on every negative half such as
 * `-0.125`, which is exactly where allocation remainders and credits live.
 * Re-exported under this name so existing imports keep working.
 */
export function roundCurrency(value: number): number {
  return roundMoney(value)
}

/**
 * Format a number as tenant-currency with 2 decimal places and comma separators.
 * Examples (AED tenant): formatAmount(25000) → "AED 25,000.00"
 *                        formatAmount(-8200) → "AED -8,200.00"
 *                        formatAmount(0) → "AED 0.00"
 */
export function formatAmount(value: number | null | undefined): string {
  const { currency, locale } = getTenantMoney()
  if (value == null) return `${currency} 0.00`
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value))
  return value < 0 ? `${currency} -${formatted}` : `${currency} ${formatted}`
}

/**
 * Format amount without the currency prefix (for use in tables where prefix is in header).
 */
export function formatAmountShort(value: number | null | undefined): string {
  if (value == null) return '0.00'
  const { locale } = getTenantMoney()
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a date string (ISO) to DD/MM/YYYY (UAE format).
 */
export function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '-'
  const date = new Date(isoDate)
  if (isNaN(date.getTime())) return '-'
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

/**
 * Format a date string to a short form (e.g., "15 Jan 2026").
 */
export function formatDateShort(isoDate: string | null | undefined): string {
  if (!isoDate) return '-'
  const date = new Date(isoDate)
  if (isNaN(date.getTime())) return '-'
  return date.toLocaleDateString(getTenantMoney().locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Format a timestamp to "DD/MM/YYYY HH:mm".
 */
export function formatDateTime(isoTimestamp: string | null | undefined): string {
  if (!isoTimestamp) return '-'
  const date = new Date(isoTimestamp)
  if (isNaN(date.getTime())) return '-'
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${day}/${month}/${year} ${hours}:${minutes}`
}

/**
 * Format relative time (e.g., "2 hours ago", "3 days ago").
 */
export function formatRelativeTime(isoTimestamp: string | null | undefined): string {
  if (!isoTimestamp) return '-'
  const date = new Date(isoTimestamp)
  if (isNaN(date.getTime())) return '-'

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) return `${diffDays}d ago`
  if (diffHours > 0) return `${diffHours}h ago`
  if (diffMinutes > 0) return `${diffMinutes}m ago`
  return 'just now'
}

/**
 * Format a percentage value (e.g., 0.75 → "75.0%").
 */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null) return '-'
  return `${(value * 100).toFixed(decimals)}%`
}

/**
 * Format a voucher nature to a user-friendly label.
 */
export function formatVoucherNature(nature: string): string {
  const map: Record<string, string> = {
    PAYMENT: 'Payment',
    RECEIPT: 'Receipt',
    INVOICE: 'Invoice',
    JOURNAL: 'Journal',
    CREDIT_NOTE: 'Credit Note',
    DEBIT_NOTE: 'Debit Note',
  }
  return map[nature] ?? nature
}
